import { ArrowLeftIcon, CoinsIcon, XIcon } from "lucide-react";

import { Column } from "../Column";
import { Row } from "../Row";
import { Text } from "../Text";
import { Button } from "../ui/button";
import {
  DialogClose,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import type {
  WeeklyCoinReport,
  WeeklyCoinReportPeriod,
} from "@/hooks/queries/useGetWeeklyCoinReport";
import { cn } from "@/lib/utils";

const headlineCategories = [
  ["PLACEMENT", "Placements"],
  ["SEARCH_SUBMIT", "Search submits"],
  ["STANDARD_SUBMIT", "Submits"],
  ["SEARCH_SHORTLIST", "Shortlists"],
  ["STREAK_COINS", "Streak coins"],
] as const;

const tableCategories = [
  ...headlineCategories,
  ["INTEREST_GOAL_BONUS", "Interest goal"],
  ["SHORTLIST_GOAL_BONUS", "Shortlist goal"],
  ["INTEREST_MILESTONE", "Interest milestone"],
  ["OTHER_INCOME", "Other"],
] as const;

const stafferName = (staffer: {
  firstName: string | null;
  lastName: string | null;
}) => `${staffer.firstName ?? ""} ${staffer.lastName ?? ""}`.trim();

export const StafferPetWeeklyCoinReportPage = ({
  report,
  period,
  onPeriodChange,
  onBack,
}: {
  report: WeeklyCoinReport | undefined;
  period: WeeklyCoinReportPeriod;
  onPeriodChange: (period: WeeklyCoinReportPeriod) => void;
  onBack: () => void;
}) => {
  const topEarners = report?.staffers.filter(
    (staffer) => staffer.coinsEarned === report.staffers[0]?.coinsEarned,
  );

  return (
    <>
      <DialogHeader className="border-b-4 border-slate-900 pb-3 text-left">
        <Row className="w-full items-center justify-between gap-4">
          <Column className="gap-0">
            <DialogTitle className="font-mono text-xl font-black uppercase tracking-[0.1em] text-slate-900">
              The Treasury
            </DialogTitle>
            <DialogDescription className="font-mono text-xs uppercase tracking-[0.18em] text-orange-600">
              Track the team coin haul
            </DialogDescription>
          </Column>
          <DialogClose asChild>
            <Button type="button" variant="ghost" className="h-8 w-8 p-0">
              <XIcon />
            </Button>
          </DialogClose>
        </Row>
      </DialogHeader>

      {/* One wrapper keeps the body in the dialog grid's single flexible row. */}
      <Column className="min-h-0 gap-0">
        {/* Folder tabs: they sit on the top-earner card's border, and the
            active one merges into the card by dropping its bottom edge. */}
        <Row className="z-10 -mb-1 shrink-0 flex-wrap gap-1 pl-3">
          {(
            [
              ["LAST_MONTH", "Last month"],
              ["LAST_WEEK", "Last week"],
              ["THIS_WEEK", "This week"],
              ["THIS_MONTH", "This month"],
            ] as const
          ).map(([value, label]) => (
            <Button
              key={value}
              type="button"
              variant="ghost"
              onClick={() => {
                onPeriodChange(value);
              }}
              className={cn(
                "h-8 shrink-0 whitespace-nowrap rounded-none border-4 border-b-0 border-slate-900 px-3 font-mono text-[10px] font-black uppercase tracking-[0.08em]",
                period === value
                  ? "bg-amber-300 text-slate-900 hover:bg-amber-300"
                  : "mt-1 h-7 bg-white text-slate-600 hover:bg-orange-50",
              )}
            >
              {label}
            </Button>
          ))}
        </Row>
        {report === undefined ? (
          <Text className="py-16 text-center font-mono text-xs font-black uppercase tracking-[0.12em] text-slate-500">
            Counting coins…
          </Text>
        ) : (
          <Column className="min-h-0 flex-1 gap-4 overflow-y-auto pr-1">
            <Row className="items-stretch gap-3">
              <Column className="flex-1 border-4 border-slate-900 bg-amber-300 p-5 shadow-[5px_5px_0_#172033]">
                <Text className="font-mono text-[10px] font-black uppercase tracking-[0.14em] text-slate-700">
                  Top earner of the week
                </Text>
                <Text className="mt-2 font-mono text-2xl font-black uppercase tracking-[0.04em] text-slate-900">
                  {topEarners?.map(stafferName).join(" & ") ?? "No earner yet"}
                </Text>
                <Row className="mt-3 items-center gap-2">
                  <CoinsIcon className="h-5 w-5 text-amber-700" />
                  <Text className="font-mono text-lg font-black text-slate-900">
                    {(topEarners?.[0]?.coinsEarned ?? 0).toLocaleString(
                      "en-US",
                    )}{" "}
                    coins
                  </Text>
                </Row>
              </Column>
              <Column className="w-40 border-4 border-slate-900 bg-violet-200 p-5 shadow-[5px_5px_0_#172033]">
                <Text className="font-mono text-[10px] font-black uppercase tracking-[0.12em] text-slate-700">
                  Team haul
                </Text>
                <Text className="mt-2 font-mono text-3xl font-black text-slate-900">
                  {report.totalCoinsEarned.toLocaleString("en-US")}
                </Text>
              </Column>
            </Row>
            <div className="grid grid-cols-5 gap-2">
              {headlineCategories.map(([key, label]) => {
                const highest = Math.max(
                  0,
                  ...report.staffers.map(
                    (staffer) => staffer.categoryTotals[key] ?? 0,
                  ),
                );
                const leaders = report.staffers
                  .filter((staffer) => staffer.categoryTotals[key] === highest)
                  .map(stafferName)
                  .join(" & ");
                return (
                  <Column
                    key={key}
                    className="border-2 border-slate-900 bg-white p-2 shadow-[2px_2px_0_#172033]"
                  >
                    <Text className="font-mono text-[8px] font-black uppercase tracking-[0.06em] text-slate-500">
                      {label}
                    </Text>
                    <Text className="mt-1 truncate font-mono text-[10px] font-black uppercase text-slate-900">
                      {highest === 0 ? "No winner" : leaders}
                    </Text>
                    <Text className="font-mono text-sm font-black text-amber-700">
                      {highest.toLocaleString("en-US")} coins
                    </Text>
                  </Column>
                );
              })}
            </div>
            <Column className="overflow-hidden border-4 border-slate-900 bg-white shadow-[4px_4px_0_#172033]">
              <Row className="border-b-2 border-slate-900 bg-slate-900 px-3 py-2 text-white">
                <Text className="font-mono text-[10px] uppercase tracking-[0.12em]">
                  Scoreboard
                </Text>
              </Row>
              <div className="max-h-72 overflow-auto">
                <table className="w-full border-collapse font-mono text-[10px]">
                  <thead className="sticky top-0 bg-amber-100 text-slate-900">
                    <tr className="border-b-2 border-slate-900">
                      <th className="px-2 py-2 text-left">#</th>
                      <th className="px-2 py-2 text-left">Staffer</th>
                      <th className="px-2 py-2 text-right">Weekly</th>
                      {tableCategories.map(([key, label]) => (
                        <th
                          key={key}
                          className="whitespace-nowrap px-2 py-2 text-right"
                        >
                          {label}
                        </th>
                      ))}
                      <th className="px-2 py-2 text-right">Balance</th>
                      <th className="whitespace-nowrap px-2 py-2 text-right">
                        Lifetime
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.staffers.map((staffer, index) => (
                      <tr
                        key={staffer.userId}
                        className="border-b border-slate-200 last:border-0 even:bg-slate-50"
                      >
                        <td className="px-2 py-2 text-slate-400">
                          {index + 1}
                        </td>
                        <td className="whitespace-nowrap px-2 py-2 font-black uppercase">
                          {stafferName(staffer)}
                        </td>
                        <td className="px-2 py-2 text-right font-black text-amber-700">
                          {staffer.coinsEarned.toLocaleString("en-US")}
                        </td>
                        {tableCategories.map(([key]) => (
                          <td key={key} className="px-2 py-2 text-right">
                            {staffer.categoryTotals[key] ?? 0}
                          </td>
                        ))}
                        <td className="px-2 py-2 text-right">
                          {staffer.coinBalance.toLocaleString("en-US")}
                        </td>
                        <td className="px-2 py-2 text-right">
                          {staffer.totalCoinsEarned.toLocaleString("en-US")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Column>
          </Column>
        )}
      </Column>
      <Row className="shrink-0 border-t-4 border-slate-900 pt-4">
        <Button
          type="button"
          variant="outlineDark"
          onClick={onBack}
          className="h-12 border-2 border-slate-900 bg-white px-5 font-mono text-xs font-black uppercase tracking-[0.12em] shadow-[3px_3px_0_#172033]"
        >
          <ArrowLeftIcon className="mr-2 h-4 w-4" />
          Back to yard
        </Button>
      </Row>
    </>
  );
};
